import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import QuizAnswers from '@/components/quiz-anwsers/QuizAnswers';
import ShoppingCart from '@/components/shopping-cart/ShoppingCart';
import { DateTime } from 'luxon';
import { UserFile } from '@/models/UserFile';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { BoxRequest } from '@/models/BoxRequest';
import { isArray } from 'lodash';

@Component<Sidepanel>({
  components: {
    QuizAnswers,
    ShoppingCart,
  },
})
export default class Sidepanel extends Vue {
  public $pageTitle = 'Sidepanel';
  private drawer = null;
  private active = null;
  protected selectedImage: string = '';
  protected isShowingImage: boolean = false;

  @Prop()
  protected latestBoxRequest!: BoxRequest;

  @Prop({ default: false })
  protected contact!: any;

  @Prop({ default: false })
  protected orderItems!: String;

  @Prop()
  protected status!: string;

  protected stijlquizImages: any[] = [];

  protected isLoading = true;

  public mounted() {
    this.initialize();
  }

  protected async initialize() {
    this.isLoading = true;
    await this.fetchFiles();
    this.isLoading = false;
  }

  protected updateOrderItems() {
    this.$emit('orderItemsUpdated');
  }

  protected async fetchFiles() {
    if (!this.contact.id) {
      return;
    }

    await new UserFile()
      .user((this.contact as any).id)
      .limit(100)
      .all()
      .then((images: any) => {
        this.stijlquizImages = images;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected getLatestRequestBoxValue(key: string): string {
    if (!this.latestBoxRequest || !this.latestBoxRequest.info) {
      return '';
    }

    const value = this.latestBoxRequest.info.find((info: Record<string, any>) => {
      if (info.key === key) {
        return info.value;
      }
    })

    return value ? value.value as string : '-';
  }

  protected get age() {
    if (!this.contact || !this.contact.profile || !this.contact.profile.birthday_at) {
      return '-';
    }

    return this.formatBirthday(this.contact.profile.birthday_at);
  }

  protected get contactCupSize() {
    // Heyflow
    const result = this.getAnwser('input-rows_2_cup_size');
    if (result) {
      return `${result.value[0].value}`;
    }

    // Legacy
    const answer = this.getAnwser('input-rows_2');
    if (!answer) {
      return '-';
    }
    return `${answer.value.cup_size}${answer.value.band_size}`;
  }

  protected get contactHeight() {
    // Heyflow
    const result = this.getAnwser('input-rows_1_height');
    if (result) {
      return `${result.value[0].value}`;
    }

    // Legacy
    const answer = this.getAnwser('input-rows_1');
    if (!answer) {
      return '-';
    }

    return `${answer.value[0].value}cm`;
  }

  protected get hasLatestRequestBox() {
    return !!this.latestBoxRequest;
  }

  protected get contactJeansSize() {
    // Heyflow
    const length = this.getAnwser('input-rows_2_jeans_lengte');
    const taille = this.getAnwser('input-rows_2_jeans_taille');
    if (length && taille) {
      return `${taille.value[0].value}x${length.value[0].value}`;
    }

    // Legacy
    const answer = this.getAnwser('input-rows_2');
    if (!answer) {
      return '-';
    }
    return `${answer.value.jeans_taille}x${answer.value.jeans_lengte}`;
  }

  protected get contactTopsSize() {
    // Heyflow
    const result = this.getAnwser('input-rows_2_tops');
    if (result) {
      return `${result.value[0].value}`;
    }

    // Legacy
    const answer = this.getAnwser('input-rows_2');
    if (!answer) {
      return '-';
    }
    return `${answer.value.tops}`;
  }

  protected get contactUpperSize() {
    // Heyflow
    const result = this.getAnwser('input-rows_2_confectiemaat_boven');
    if (result) {
      return `${result.value[0].value}`;
    }

    // Legacy
    const answer = this.getAnwser('input-rows_2');
    if (!answer) {
      return '-';
    }
    return `${answer.value.confectiemaat_boven}`;
  }

  protected get contactLowerSize() {
    // Heyflow
    const result = this.getAnwser('input-rows_2_confectiemaat_onder');
    if (result) {
      return `${result.value[0].value}`;
    }

    // Legacy
    const answer = this.getAnwser('input-rows_2');
    if (!answer) {
      return '-';
    }
    return `${answer.value.confectiemaat_onder}`;
  }

  protected get styleClassic() {
    const answer = this.getAnwser('preference_1');
    if (!answer) {
      return '-';
    }
    return `${answer.value}`;
  }

  protected get styleBoho() {
    const answer = this.getAnwser('preference_2');
    if (!answer) {
      return '-';
    }
    return `${answer.value}`;
  }

  protected get styleSporty() {
    const answer = this.getAnwser('preference_3');
    if (!answer) {
      return '-';
    }
    return `${answer.value}`;
  }

  protected get styleMinimal() {
    const answer = this.getAnwser('preference_4');
    if (!answer) {
      return '-';
    }
    return `${answer.value}`;
  }

  protected get styleRomantic() {
    const answer = this.getAnwser('preference_5');
    if (!answer) {
      return '-';
    }
    return `${answer.value}`;
  }

  protected get brands() {
    const answer = this.getAnwser('brands');
    if (!answer) {
      return '-';
    }

    const brands = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return brands;
  }

  protected get keywords() {
    const answer = this.getAnwser('checkbox-list_1');
    if (!answer) {
      return '-';
    }

    const keywords = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return keywords;
  }

  protected get dresscode() {
    const answer = this.getAnwser('checkbox-list-boxes_1');
    if (!answer) {
      return '-';
    }

    const dresscode = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return dresscode;
  }

  protected get budgetPerMonth() {
    const answer = this.getAnwser('input-rows_4');
    if (!answer) {
      return '-';
    }

    // Heyflow
    if (Array.isArray(answer.value)) {
      if (!answer.value.length) {
        return '-';
      }
      return answer.value[0].value;
    }

    return answer.value.per_month;
  }

  protected get colors() {
    const answer = this.getAnwser('checkbox-list_4');
    if (!answer) {
      return '-';
    }

    const colors = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return colors;
  }

  protected get motivations() {
    const answer = this.getAnwser('checkbox-list_8');
    if (!answer) {
      return '-';
    }

    const motivations = answer.value.map((currentAnswer: any) => {
      return currentAnswer.label;
    });

    return motivations;
  }

  protected get boxPreferencesIsArray() {
    const answer = this.getAnwser('radio-list-boxes_4');
    if (!answer) {
      return false
    }

    return Array.isArray(answer.value);
  }

  protected get boxPreferences() {
    const answer = this.getAnwser('radio-list-boxes_4');
    if (!answer) {
      return '-';
    }

    // const wishlist = answer.value.map((currentAnswer: any) => {
    //   return currentAnswer.label;
    // }).join(', ');

    return answer.value;
  }

  protected get boxWishlist() {
    const answer = this.getAnwser('checkbox-list_wishlist');
    if (!answer) {
      return '-';
    }

    let wishlist: any;
    if (isArray(answer.value)) {
    wishlist = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

      return wishlist;
    }

    if (typeof answer.value === 'object') {
      wishlist = answer.value.label;
      return wishlist;
    }

    wishlist = answer.value;
    return wishlist;
  }

  protected get boxNotes() {
    const answer = this.getAnwser('textarea_1');
    if (!answer) {
      return '-';
    }

    return answer.value;
  }

  protected get colorsDont() {
    const answer = this.getAnwser('checkbox-list_5');
    if (!answer) {
      return '-';
    }

    const colors = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return colors;
  }

  protected get fabricsDont() {
    const answer = this.getAnwser('checkbox-list_6');
    if (!answer) {
      return '-';
    }

    const fabrics = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return fabrics;
  }

  protected get printsDont() {
    const answer = this.getAnwser('checkbox-list_7');
    if (!answer) {
      return '-';
    }

    const prints = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return prints;
  }

  protected get camouflage() {
    let camouflageList: string[] = [];
    const answers = this.getAnwsers([
      // Legacy
      'radio-list-pill_1',
      'radio-list-pill_2',
      'radio-list-pill_3',
      // Heyflow
      'radio-list-pill_1_Armen',
      'radio-list-pill_1_schouders',
      'radio-list-pill_1_halslijn',
      'radio-list-pill_2_taille',
      'radio-list-pill_2_buik',
      'radio-list-pill_3_benen',
      'radio-list-pill_3_rug',
    ]);
    answers.forEach((answer: any) => {
      Object.keys(answer.value).forEach((key: string) => {
        if (answer.value[key].label.toLowerCase().includes('camoufleren')) {
          // Heyflow
          camouflageList.push(answer.value[key].label);
        }
      });
    });
    return camouflageList.join(', ');
  }

  protected getClothingPreferences(key: string) {
    const answer = this.getAnwser(key);
    if (!answer) {
      return '-';
    }

    return answer.value;
  }

  protected getCheckboxAnswer(key: string) {
    const answer = this.getAnwser(key);
    if (!answer || !answer.value[0]) {
      return '-';
    }

    return answer.value[0].key;
  }

  protected getBudget(type: string) {
    // Heyflow
    const result = this.getAnwser(`input-rows_3_${type === 'trousers' ? 'broeken' : type}`);
    if (result) {
      return this.stripCurrency(`${result.value[0].value}`);
    }

    // Legacy
    const answer = this.getAnwser('input-rows_3');
    if (!answer) {
      return '-';
    }

    return this.stripCurrency(answer.value[type]);
  }

  private stripCurrency(value: string): string {
    if (!value) return '-';
    return value
      .replace(/\\u20ac/gi, '')
      .replace(/\\u00a0/g, '')
      .replace(/[€$\u00a0\u20ac]/g, '')
      .trim();
  }

  protected get topsFit() {
    const answer = this.getAnwser('checkbox-list_2');

    if (!answer) {
      return '-';
    }

    const fit = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return fit;
  }

  protected get jeansFit() {
    const answer = this.getAnwser('radio-list-boxes_1');
    if (!answer) {
      return '-';
    }

    // Heyflow
    if (Array.isArray(answer.value)) {
      if (!answer.value.length) {
        return '-';
      }
      return answer.value[0].label;
    }

    return answer.value.label;
  }

  protected get dressesFit() {
    const answer = this.getAnwser('checkbox-list_3');
    if (!answer) {
      return '-';
    }

    const fit = answer.value
      .map((currentAnswer: any) => {
        return currentAnswer.label;
      })
      .join(', ');

    return fit;
  }

  protected get accessoiresPreferences() {
    const answer = this.getAnwser('radio-list-boxes_2');
    if (!answer) {
      return '-';
    }

    // Heyflow
    if (Array.isArray(answer.value)) {
      if (!answer.value.length) {
        return '-';
      }
      return answer.value[0].label;
    }

    // Legacy
    return answer.value.label;
  }

  protected get accessoiresColor() {
    const answer = this.getAnwser('radio-list-boxes_22');
    if (!answer) {
      return '-';
    }

    // Heyflow
    if (Array.isArray(answer.value)) {
      if (!answer.value.length) {
        return '-';
      }
      return answer.value[0].label;
    }

    // Legacy
    return answer.value.label;
  }

  protected get canWearEarrings() {
    const answer = this.getAnwser('radio-list-boxes_3');
    if (!answer) {
      return '-';
    }

    // Heyflow
    if (Array.isArray(answer.value)) {
      if (!answer.value.length) {
        return '-';
      }
      return answer.value[0].label;
    }

    // Legacy
    return answer.value.label;
  }

  protected get answers() {
    return this.contact && this.contact.profile && this.contact.profile.answers ? this.contact.profile.answers : [];
  }

  private getAnwsers(keys: string[]) {
    return this.answers.filter((answer: any) => {
      return keys.includes(answer.key);
    });
  }

  private getAnwser(key: string) {
    return this.answers.find((answer: any) => {
      return answer.key === key;
    });
  }

  formatBirthday(date: string) {
    if (!date) {
      return '';
    }

    const birthday = DateTime.fromFormat(date, 'yyyy-LL-dd');
    if (!birthday.isValid) {
      return date;
    }

    const now = DateTime.local();
    return Math.floor(Math.abs(birthday.diff(now, 'years').toObject().years || 0));
  }

  @Watch('contact')
  protected contactChanged() {
    this.fetchFiles();
  }
}
